import { useEffect, useState, useRef } from "react";
import { api, API, authHeader } from "@/lib/api";
import { toast } from "sonner";
import { Images, Upload, Check, Trash2, Loader2, Clock } from "lucide-react";

export const AdminGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    api
      .get("/admin/photos", { headers: authHeader() })
      .then((r) => setPhotos(r.data))
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/photos", fd, { headers: authHeader() });
      toast.success("Foto caricata");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const approve = async (id) => {
    await api.put(`/admin/photos/${id}/approve`, {}, { headers: authHeader() });
    toast.success("Foto approvata");
    load();
  };

  const remove = async (id) => {
    await api.delete(`/admin/photos/${id}`, { headers: authHeader() });
    toast.success("Foto rimossa");
    load();
  };

  const pending = photos.filter((p) => !p.approved);

  return (
    <div className="bg-[#0f172a] border border-[#d4af37]/20 rounded-md p-5">
      <h3 className="font-serif text-2xl text-[#d4af37] flex items-center gap-2 mb-2">
        <Images className="w-5 h-5" /> Galleria foto
      </h3>
      <p className="text-xs text-[#e0ddcf]/70 mb-4">
        Carica le tue foto (pubblicate subito). Le foto dei clienti restano in
        attesa finché non le approvi.
      </p>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        data-testid="admin-photo-input"
        onChange={(e) => upload(e.target.files[0])}
        className="hidden"
      />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        data-testid="admin-upload-photo-button"
        className="w-full bg-[#d4af37] text-[#060a14] font-semibold px-4 py-2.5 rounded-sm active:scale-95 transition-transform flex items-center justify-center gap-2 disabled:opacity-50 text-sm mb-4"
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            <Upload className="w-4 h-4" /> Carica una foto
          </>
        )}
      </button>

      {pending.length > 0 && (
        <div className="mb-4">
          <p className="text-[11px] uppercase tracking-[0.15em] text-[#d4af37]/80 mb-2 flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> In attesa ({pending.length})
          </p>
          <div className="grid grid-cols-3 gap-2">
            {pending.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-pending-${p.id}`}
                className="relative aspect-square rounded-sm overflow-hidden border border-amber-400/40"
              >
                <img
                  src={`${API}/photos/${p.id}/image`}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 flex">
                  <button
                    onClick={() => approve(p.id)}
                    data-testid={`approve-photo-${p.id}`}
                    className="flex-1 bg-green-600/90 text-white py-1.5 flex items-center justify-center"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(p.id)}
                    data-testid={`delete-photo-${p.id}`}
                    className="flex-1 bg-red-600/90 text-white py-1.5 flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {p.name && (
                  <span className="absolute top-0 inset-x-0 bg-black/50 text-[9px] text-white px-1 truncate">
                    {p.name}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        {photos
          .filter((p) => p.approved)
          .map((p) => (
            <div
              key={p.id}
              className="relative aspect-square rounded-sm overflow-hidden border border-[#d4af37]/20 group"
            >
              <img
                src={`${API}/photos/${p.id}/image`}
                alt=""
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => remove(p.id)}
                data-testid={`delete-photo-${p.id}`}
                className="absolute top-1 right-1 bg-red-600/90 text-white p-1 rounded-sm opacity-90"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              {p.source === "customer" && (
                <span className="absolute bottom-0 inset-x-0 bg-black/50 text-[8px] text-white px-1 truncate">
                  {p.name || "cliente"}
                </span>
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

export default AdminGallery;
